import React from 'react';

import styled from '@emotion/styled';
import { borderRadius, colors } from '@rtkwlf/fenrir-react';
import { ScannerConnectionStatus } from '@rtkwlf/rendall';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useScannerProfile } from '@apis/rendall/queries/use-scanner-profile';
import { Grid, Stack, Typography } from '@packages/fenrir/components';

import type { ScannerDetailsForm } from '../../../../types';

import { ScannerConfigurationDropdown } from './components/scanner-configuration-dropdown';
import { ScannerConfigurationSwitch } from './components/scanner-configuration-switch';
import { useHostIdScanIntensityOptions } from './components/use-host-id-scan-intensity-options';
import { useMaxConcurrentVulScansOptions } from './components/use-max-concurrent-vul-scans-options';
import { useScanPeriodOptions } from './components/use-scan-period-options';

const FieldSet = styled.fieldset<{ width?: string }>(({ width = '70%' }) => ({
  border: `1px solid rgb(${colors.border.neutral.default})`,
  borderRadius: borderRadius.default,
  height: 'fit-content',
  minWidth: '0',
  padding: '20px',
  display: 'grid',
  width,
}));

export const ScannerConfigurationV2 = () => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { scannerId = '' } = useParams();
  const { data: scannerProfile, isPending } = useScannerProfile(scannerId);
  const { control, watch } = useFormContext<ScannerDetailsForm>();
  const formData = watch();
  const isHostIdenOn = formData.hostIden;
  const isVulScanOn = formData.vulnScan;
  const isScannerConnected =
    scannerProfile?.scannerDetails?.connectionStatus ===
    ScannerConnectionStatus.Connected;

  const hostIdentificationScanningIntensityOptions =
    useHostIdScanIntensityOptions();
  const scanPeriodOptions = useScanPeriodOptions();
  const maxConcurrentVulScansOptions = useMaxConcurrentVulScansOptions();
  const hasScanOutsideWindowDisabledCapability =
    scannerProfile?.scannerDetails?.capabilities.includes(
      'scan_outside_window_disabled',
    );

  return (
    <Stack
      data-testid="main-scanner-config-column"
      gap={4}
      width="100%"
      marginBottom={4}
    >
      <Typography variant="h4">{t('scannerConfiguration.title')}</Typography>
      <Grid container maxWidth="100rem" gap={2.5}>
        <FieldSet>
          <ScannerConfigurationSwitch
            control={control}
            controlName="hostIden"
            label={t('scannerConfiguration.hostIdentification')}
            description={t(
              'scannerConfiguration.hostIdentificationDescription',
            )}
            isPending={isPending}
            isDisabled={!isScannerConnected}
            isFieldSetHeader={true}
            gridSize={12}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            marginBottom={1}
            flexWrap="wrap"
          >
            <ScannerConfigurationSwitch
              control={control}
              controlName="pingOnly"
              label={t('scannerConfiguration.pingOnlyDiscovery')}
              description={t(
                'scannerConfiguration.pingOnlyDiscoveryDescription',
              )}
              isPending={isPending}
              isDisabled={!isScannerConnected || !isHostIdenOn}
            />
            <ScannerConfigurationDropdown
              control={control}
              controlName="hostIdentificationScanIntensity"
              label={t('hostIdentificationScanIntensity.label')}
              description={t('hostIdentificationScanIntensity.description')}
              placeholder={t('scannerConfiguration.placeholder')}
              options={hostIdentificationScanningIntensityOptions}
              isPending={isPending}
              isDisabled={
                !hasScanOutsideWindowDisabledCapability || !isHostIdenOn
              }
            />
            <ScannerConfigurationDropdown
              control={control}
              controlName="hostIdentificationScanPeriod"
              label={t('hostIdentificationScanPeriod.label')}
              description={t('hostIdentificationScanPeriod.description')}
              placeholder={t('scannerConfiguration.placeholder')}
              options={scanPeriodOptions}
              isPending={isPending}
              isDisabled={
                !hasScanOutsideWindowDisabledCapability || !isHostIdenOn
              }
            />
          </Stack>
          <FieldSet width="100%">
            <ScannerConfigurationSwitch
              control={control}
              controlName="vulnScan"
              label={t('scannerConfiguration.vulnerabilityScanning')}
              description={t(
                'scannerConfiguration.vulnerabilityScanningDescription',
              )}
              isPending={isPending}
              isDisabled={!isScannerConnected || !isHostIdenOn}
              isFieldSetHeader={true}
              gridSize={12}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <ScannerConfigurationSwitch
                control={control}
                controlName="cgiScan"
                label={t('scannerConfiguration.cgiScanning')}
                description={t('scannerConfiguration.cgiScanningDescription')}
                isPending={isPending}
                isDisabled={
                  !isScannerConnected || !isHostIdenOn || !isVulScanOn
                }
              />
              <ScannerConfigurationSwitch
                control={control}
                controlName="bruteScan"
                label={t('scannerConfiguration.bruteForceScanning')}
                description={t(
                  'scannerConfiguration.bruteForceScanningDescription',
                )}
                isPending={isPending}
                isDisabled={
                  !isScannerConnected || !isHostIdenOn || !isVulScanOn
                }
              />
              <ScannerConfigurationDropdown
                control={control}
                controlName="maxConcurrentVulnerabilityScans"
                label={t('maxConcurrentVulnerabilityScans.label')}
                description={t('maxConcurrentVulnerabilityScans.description')}
                placeholder={t('scannerConfiguration.placeholder')}
                options={maxConcurrentVulScansOptions}
                isPending={isPending}
                isDisabled={
                  !hasScanOutsideWindowDisabledCapability ||
                  !isHostIdenOn ||
                  !isVulScanOn
                }
              />
            </Stack>
          </FieldSet>
        </FieldSet>
      </Grid>
    </Stack>
  );
};
